<template>
  <v-card class="soft-card mt-4" style="margin-top: 16px !important; padding: 14px 0px !important;">
    <v-card-title class="pa-4" style="padding: 0px 10px 14px !important;">
      <v-layout row wrap align-center justify-space-between>
        <div>
          <div class="table-title">Consolidated Portfolio Report</div>
          <div class="table-subtitle">All syndicated deals for the selected account.</div>
        </div>
        <v-layout row align-center justify-end style="max-width: 480px;">
          <v-text-field
            v-model="search"
            @input="onSearch"
            append-icon="search"
            placeholder="Search syndicate, company, MID, status"
            solo
            flat
            hide-details
            single-line
            class="sw-input"
            style="max-width: 320px;"
          ></v-text-field>
          <v-btn small color="green darken-2" dark class="export-btn ml-2" :loading="exporting" @click="exportExcel">
            <v-icon small left>vertical_align_bottom</v-icon>Excel
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card-title>
    <v-divider></v-divider>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" class="my-0"></v-progress-linear>

    <v-data-table
      class="report-table"
      :headers="headers"
      :items="rows"
      :total-items="totalItems"
      :loading="loading"
      :rows-per-page-items="[25, 50, 100, { text: 'All', value: -1 }]"
      :pagination.sync="pagination"
    >
      <template slot="items" slot-scope="props">
        <td class="bold-dark" style="text-align: left;">{{ dash(props.item.syndicate_name) }}</td>
        <td style="text-align: left;">{{ dash(props.item.company_name) }}</td>
        <td style="text-align: left;">{{ dash(props.item.contract_MID) }}</td>
        <td style="text-align: left;">{{ formatDate(props.item.funded_date) }}</td>
        <td style="text-align: left;">{{ props.item.capital_participated | currency }}</td>
        <td style="text-align: left;">{{ props.item.participation_percent }}%</td>
        <td style="text-align: left;">{{ dash(props.item.factor_rate) }}</td>
        <td style="text-align: left;">{{ props.item.part_rtr | currency }}</td>
        <td style="text-align: left;">{{ props.item.collected | currency }}</td>
        <td style="text-align: left;">{{ props.item.remaining | currency }}</td>
        <td style="text-align: left;">{{ props.item.total_payout | currency }}</td>
        <td class="blue-text-custom" style="text-align: left;">{{ props.item.projected_profit | currency }}</td>
        <td class="green-text-custom" style="text-align: left;">{{ props.item.actual_profit | currency }}</td>
        <td style="text-align: left;">
          <v-chip small :color="statusTone(props.item.status).bg" :text-color="statusTone(props.item.status).text" class="chip-tight">{{ dash(props.item.status) }}</v-chip>
        </td>
        <td style="text-align: left;">
          <v-chip v-if="has(props.item.pace)" small :color="paceTone(props.item.pace).bg" :text-color="paceTone(props.item.pace).text" class="chip-tight">{{ props.item.pace }}</v-chip>
          <span v-else>-</span>
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import axios from 'axios'
import { URL } from '../../../env'
import moment from 'moment'

export default {
  props: {
    syndicationId: { default: null },
  },
  data() {
    return {
      syndication_id: this.syndicationId || localStorage.getItem('syndication_id'),
      user_id: localStorage.getItem('user_id'),
      rows: [],
      totalItems: 0,
      loading: false,
      exporting: false,
      search: '',
      pagination: {
        sortBy: 'funded_date',
        descending: true,
        page: 1,
        rowsPerPage: 25,
      },
      headers: [
        { text: 'Syndicate Name', align: 'left', value: 'syndicate_name' },
        { text: 'Company Name', align: 'left', value: 'company_name' },
        { text: 'MID', align: 'left', value: 'contract_MID' },
        { text: 'Funded Date', align: 'left', value: 'funded_date' },
        { text: 'Capital Participated', align: 'left', value: 'capital_participated' },
        { text: 'Participation %', align: 'left', value: 'participation_percent' },
        { text: 'Factor Rate', align: 'left', value: 'factor_rate' },
        { text: 'Part RTR', align: 'left', value: 'part_rtr' },
        { text: 'Collected', align: 'left', value: 'collected', sortable: false },
        { text: 'Remaining Balance', align: 'left', value: 'remaining' },
        { text: 'Total Payout', align: 'left', value: 'total_payout', sortable: false },
        { text: 'Projected Profit', align: 'left', value: 'projected_profit', sortable: false },
        { text: 'Actual Profit', align: 'left', value: 'actual_profit', sortable: false },
        { text: 'Status', align: 'left', value: 'status' },
        { text: 'Pace', align: 'left', value: 'pace', sortable: false },
      ],
    };
  },
  mounted() {
    this.getPortfolio();
  },
  watch: {
    // Re-fetch when the table changes page / rows-per-page / sort column.
    pagination: {
      handler() { this.getPortfolio(); },
      deep: true,
    },
    // Re-fetch when a different syndicate is selected.
    syndicationId: function (id) {
      this.syndication_id = id;
      this.pagination.page = 1;
      this._lastKey = null; // syndication_id isn't in the dedupe key, so force a refetch
      this.getPortfolio();
    },
  },
  methods: {
    getPortfolio: function () {
      if (!this.syndication_id) return;
      const params = {
        user_id: this.user_id,
        search: this.search || null,
        page: this.pagination.page,
        length: this.pagination.rowsPerPage,
        sortBy: this.pagination.sortBy,
        descending: this.pagination.descending,
      };
      // Dedupe identical consecutive requests (mounted + watcher + search can
      // otherwise fire the same query more than once).
      const key = JSON.stringify(params);
      if (key === this._lastKey) return;
      this._lastKey = key;

      this.loading = true;
      axios.get(`${URL}/api/payouts/super_syndicate_portfolio/${this.syndication_id}`, { params })
        .then(res => {
          this.rows = (res.data && res.data.data) || [];
          this.totalItems = (res.data && res.data.total) || 0;
          this.loading = false;
        }).catch(() => { this.loading = false; });
    },
    onSearch: function () {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.pagination.page = 1; // reset to first page; watcher refetches if page changed
        this.getPortfolio();
      }, 600);
    },
    exportExcel: function () {
      if (!this.syndication_id) return;
      this.exporting = true;
      axios({
        url: `${URL}/api/payouts/super_syndicate_portfolio/${this.syndication_id}`,
        method: 'GET',
        responseType: 'blob',
        params: {
          export: 1,
          user_id: this.user_id,
          search: this.search || null,
          sortBy: this.pagination.sortBy,
          descending: this.pagination.descending,
        },
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'consolidated_portfolio_report.xlsx');
        document.body.appendChild(link);
        link.click();
        this.exporting = false;
      }).catch(() => { this.exporting = false; });
    },
    // Status colors: Complete = green, Legal = red, Collection = yellow, Active = blue.
    statusTone: function (status) {
      const s = (status || '').toLowerCase();
      if (s.indexOf('complete') > -1) return { bg: 'green lighten-5', text: 'green darken-3' };
      if (s.indexOf('legal') > -1) return { bg: 'red lighten-5', text: 'red darken-3' };
      if (s.indexOf('collection') > -1) return { bg: 'amber lighten-5', text: 'amber darken-4' };
      if (s.indexOf('active') > -1) return { bg: 'blue lighten-5', text: 'blue darken-3' };
      return { bg: 'grey lighten-3', text: 'blue-grey darken-2' };
    },
    // Pace colors: Ahead = green, On Pace = blue, Slow Pay = yellow, Behind = red.
    paceTone: function (pace) {
      switch (pace) {
        case 'Ahead': return { bg: 'green lighten-5', text: 'green darken-3' };
        case 'On Pace': return { bg: 'blue lighten-5', text: 'blue darken-3' };
        case 'Slow Pay': return { bg: 'amber lighten-5', text: 'amber darken-4' };
        case 'Behind': return { bg: 'red lighten-5', text: 'red darken-3' };
        default: return { bg: 'grey lighten-3', text: 'blue-grey darken-2' };
      }
    },
    has: function (v) {
      return v !== null && v !== undefined && v !== '';
    },
    dash: function (v) {
      return this.has(v) ? v : '-';
    },
    formatDate: function (date) {
      if (!this.has(date)) return '-';
      const m = moment(date);
      return m.isValid() ? m.format('MMM DD, YYYY') : '-';
    },
  },
};
</script>

<style scoped src="./Styles/SuperSyndicate.css"></style>
